'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { I18nextProvider, useTranslation as useI18nextTranslation } from 'react-i18next'
import i18n from '@/lib/i18n'

type LanguageContextType = {
  locale: string
  setLocale: (locale: string) => void
}

const LanguageContext = createContext<LanguageContextType>({
  locale: 'fr',
  setLocale: () => { },
})

function updateDocumentDirection(locale: string) {
  if (typeof document !== 'undefined') {
    const dir = locale === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', dir)
    document.documentElement.setAttribute('lang', locale)
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState('fr')

  useEffect(() => {
    const savedLocale = localStorage.getItem('djotta-locale') || 'fr'
    setLocaleState(savedLocale)
    i18n.changeLanguage(savedLocale)
    updateDocumentDirection(savedLocale)
  }, [])

  const setLocale = (newLocale: string) => {
    setLocaleState(newLocale)
    localStorage.setItem('djotta-locale', newLocale)
    i18n.changeLanguage(newLocale)
    updateDocumentDirection(newLocale)
  }

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={{ locale, setLocale }}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  )
}

export function useLanguage() {
  return useContext(LanguageContext)
}

// Special type for our translation function with raw support
export interface TranslationFunction {
  (key: string, options?: any): string;
  raw: (key: string) => unknown;
}

export function useTranslations(namespace?: string): TranslationFunction {
  const { t, i18n } = useI18nextTranslation()

  if (namespace) {
    const translate = (key: string, options?: any) => t(`${namespace}.${key}`, options)
    const raw = (key: string) => {
      const fullKey = `${namespace}.${key}`
      return i18n.t(fullKey, { returnObjects: true })
    }
    return Object.assign(translate, { raw }) as TranslationFunction
  }

  return Object.assign((key: string, options?: any) => t(key, options), {
    raw: (key: string) => i18n.t(key, { returnObjects: true })
  }) as TranslationFunction
}
